import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@apollo/client';
import { Select, Typography } from '@guildeducationinc/recess';
import { GET_PAYMENT_SUMMARY } from '@src/graphql/queries/paymentRequest';
import { useClientConfig, useLocale } from 'pe-core-frontend';
import { useEffect, useMemo, useState } from 'react';
import styles from './PaymentSummary.module.scss';
// The backend returns UNLIMITED_FUNDING_CAP (10^12) for unlimited policies;
// anything at/above this sentinel is rendered as "Unlimited" rather than a number.
const UNLIMITED_FUNDING_CAP = 1000000000000;
const periodKey = (ps) => `${ps.year}::${ps.segment ?? ''}`;
export const PaymentSummary = ({ employeeId, clientId }) => {
    const { t, formatCurrency, formatDateAsUTC } = useLocale();
    const { clientConfig } = useClientConfig({ clientId });
    const currency = clientConfig?.currency || 'USD';
    const { data, loading, error } = useQuery(GET_PAYMENT_SUMMARY, {
        variables: { employeeId },
    });
    const paymentSummaries = useMemo(() => data?.getPaymentSummary || [], [data]);
    const [selectedKey, setSelectedKey] = useState('');
    // Default to the current benefit period once data loads (falling back to the
    // most recent one), while preserving a valid selection the admin has made.
    useEffect(() => {
        if (!paymentSummaries.length)
            return;
        setSelectedKey(prev => {
            if (prev && paymentSummaries.some(ps => periodKey(ps) === prev))
                return prev;
            const current = paymentSummaries.find(ps => ps.isCurrentPeriod);
            return periodKey(current ?? paymentSummaries[0]);
        });
    }, [paymentSummaries]);
    const formatPeriodRange = (ps) => {
        const range = `${formatDateAsUTC(ps.periodStart, {
            dateStyle: 'medium',
        })} - ${formatDateAsUTC(ps.periodEnd, { dateStyle: 'medium' })}`;
        return ps.segment ? `${range} (${t('Segment')} ${ps.segment})` : range;
    };
    const periodOptions = useMemo(() => paymentSummaries.map(ps => {
        const range = `${formatDateAsUTC(ps.periodStart, {
            dateStyle: 'medium',
        })} - ${formatDateAsUTC(ps.periodEnd, { dateStyle: 'medium' })}`;
        return {
            value: periodKey(ps),
            label: ps.segment ? `${range} (${t('Segment')} ${ps.segment})` : range,
        };
    }), [paymentSummaries, formatDateAsUTC, t]);
    const selectedPeriod = useMemo(() => paymentSummaries.find(ps => periodKey(ps) === selectedKey), [paymentSummaries, selectedKey]);
    if (loading)
        return null;
    if (error) {
        return _jsx("div", { children: t('Unable to fetch payment details') });
    }
    if (!selectedPeriod)
        return null;
    const formatAvailable = (cap) => cap >= UNLIMITED_FUNDING_CAP ? t('Unlimited') : formatCurrency(cap, currency, true);
    const fields = [
        {
            label: t('Total requested'),
            value: formatCurrency(selectedPeriod.totalRequested, currency, true),
        },
        {
            label: t('Total paid'),
            value: formatCurrency(selectedPeriod.totalPaid, currency, true),
        },
        {
            label: t('Total pending'),
            value: formatCurrency(selectedPeriod.totalPending, currency, true),
        },
        {
            label: t('Total period allowance'),
            value: formatAvailable(selectedPeriod.fundingCap),
        },
    ];
    const showPeriodSelect = periodOptions.length > 1;
    return (_jsxs("div", { className: styles.summary, children: [_jsxs("div", { className: styles.header, children: [_jsxs("div", { children: [_jsx(Typography, { variant: 'h2', children: t('Funding Summary') }), !showPeriodSelect && (_jsx(Typography, { variant: 'label2', weight: 'normal', children: formatPeriodRange(selectedPeriod) }))] }), showPeriodSelect && (_jsx("div", { className: styles.periodSelect, children: _jsx(Select, { label: t('Benefit period'), options: periodOptions, value: selectedKey, onChange: (e) => setSelectedKey(e.target.value ?? ''), isSearchable: false, isClearable: false }) }))] }), _jsx("div", { className: styles.fields, children: fields.map(field => (_jsxs("div", { className: styles.item, children: [_jsx(Typography, { variant: 'label2', weight: 'normal', className: styles.label, children: field.label }), _jsx(Typography, { variant: 'body1', weight: '700', children: field.value })] }, field.label))) })] }));
};
